import React, { useState, useCallback } from 'react';
import { Button, Form, InputGroup } from 'react-bootstrap';
import { FaPaperPlane, FaUser } from 'react-icons/fa';
import { useTranslation } from 'react-i18next';
import { parse, differenceInDays } from 'date-fns';
import UnifiedModal from '../UnifiedModal';
import './styles.scss';
import { parseDate } from '../../utils/dateUtils';

const ChatModal = ({ dir, shouldShow, setModalVisible, params, activeFilters, locations }) => {
  const { t, i18n } = useTranslation();
  const [message, setMessage] = useState('');
  const handleClose = useCallback(() => setModalVisible(false), [setModalVisible]);

  const currentTime = new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: false });

  const getLocationName = (code) => {
    if (!locations || !Array.isArray(locations)) return code;
    const loc = locations.find(l => String(l.locationCode) === String(code));
    return loc ? `${loc.city}` : code;
  };

  // Build personalized message
  const buildPersonalizedMessage = () => {
    if (!params) return t('chat.supportMessage');

    const { startDate, endDate, fromLocationCode, toLocationCode } = params;
    if (!startDate || !endDate || !fromLocationCode) return t('chat.supportMessage');

    // Parse dates
    const start = parseDate(startDate);
    const end = parseDate(endDate);
    const duration = differenceInDays(end, start);

    const pickupLocation = getLocationName(fromLocationCode);
    const dropoffLocation = toLocationCode && toLocationCode !== fromLocationCode ? getLocationName(toLocationCode) : null;

    const pickupDay = start.toLocaleDateString(i18n.language, { weekday: 'long', month: 'short', day: 'numeric' });
    const durationText = duration === 1 ? t('chat.oneDay') : `${duration} ${t('chat.days')}`;

    let locationText;
    if (dropoffLocation) {
      locationText = t('chat.fromTo', { from: pickupLocation, to: dropoffLocation });
    } else {
      locationText = t('chat.from', { location: pickupLocation });
    }

    let message = t('chat.personalizedMessage', { location: locationText, day: pickupDay, duration: durationText });

    return message;
  };

  const personalizedMessage = buildPersonalizedMessage();

  const sendMessage = () => {
    if (message.trim()) {
      // Replace with your actual WhatsApp number
      const phoneNumber = '972546127601'; // Real support number
      const fullMessage = message + '\n\n' + t('chat.lookingAtOffers') + '\n' + window.location.href;
      const encodedMessage = encodeURIComponent(fullMessage);
      const whatsappUrl = `https://wa.me/${phoneNumber}?text=${encodedMessage}`;
      window.open(whatsappUrl, '_blank');
      setMessage('');
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const footerButtons = [
  ];

  const customFooter = (
    <div className={`${dir === 'rtl' ? 'rtl' : ''}`}>
      <div className="chat-input">
        <InputGroup>
          <Form.Control
            as="textarea"
            rows={3}
            placeholder={t('chat.typeMessage') || 'Type your message...'}
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            onKeyDown={handleKeyPress}
            dir={dir}
          />
          <InputGroup.Append>
            <Button variant="primary" onClick={sendMessage} disabled={!message.trim()}>
              <FaPaperPlane />
            </Button>
          </InputGroup.Append>
        </InputGroup>
      </div>
    </div>
  );

  return (
    <UnifiedModal
      show={shouldShow}
      onHide={handleClose}
      title={t("chat.title")}
      dir={dir}
      footerButtons={footerButtons}
      customFooter={customFooter}
      showFooter={false}
    >
      <div className={`chat-modal-container ${dir === 'rtl' ? 'rtl' : ''}`}>
        <div className="chat-messages">
          <div className="message received">
            <div className="message-avatar">
              <FaUser />
            </div>
            <div className="message-content">
              <div className="message-header">
                <span className="message-name">{t('chat.supportName') || 'Support'}</span>
                <span className="message-time">{currentTime}</span>
              </div>
              <p>{personalizedMessage}</p>
            </div>
          </div>
        </div>
      </div>
    </UnifiedModal>
  );
};

export default ChatModal;